<template>
    <div class="container container--bordereauLivraison">  

        <Loader v-if="!ready"></Loader>

        <article v-else class="bordereau-livraison">

            <header class="bordereau-livraison__heading">
                <h1 class="woocommerce-order-details__title">Bordereau n° {{ bordereauLivraison.num }}</h1>
                <div class="bordereau-livraison__heading__date">
                    <p class="subtitle">
                        <strong>Émis le <Date :date="bordereauLivraison.createdAt" /></strong>
                    </p>
                    <p>
                        <Badge type="info">{{ bordereauLivraison.status.libelle }}</Badge>
                        <Badge type="success" v-if="bordereauLivraison.status.isFacture">Facturée</Badge>
                        <Badge type="danger" v-if="bordereauLivraison.status.isRetourne">{{ bordereauLivraison.status.retourStatutLibelle }}</Badge>
                    </p>
                </div>
                <div class="facture__heading__actions">

                    <Button class="woocommerce-button button alt"
                        :disabled="downloading || bordereauLivraison.nbDocuments < 1"
                        :loading="!!downloading"
                        @click.native="download()">
                        Télécharger
                        <i class="fa-solid fa-down-to-line"></i>
                    </Button>   

                </div>
            </header>         
            
            <div class="bordereau-livraison__details">
                <ul class="woocommerce-order-overview woocommerce-thankyou-order-details order_details">
                    <li class="woocommerce-order-overview__order order">Type: <strong>Livraison</strong></li>
                    <li class="woocommerce-order-overview__order order">Ref. client : <strong>{{ bordereauLivraison.engagementNum }}</strong></li>
                    <li class="woocommerce-order-overview__order order">
                        Commande n°:
                        <strong v-if="bordereauLivraison.commande.id">
                            <router-link :to="{ name: 'commande', params: { id: bordereauLivraison.commande.id }}">{{ bordereauLivraison.commande.num }}</router-link>
                        </strong>
                        <strong v-else>{{ bordereauLivraison.commande.num }}</strong>
                    </li>
                    <li class="woocommerce-order-overview__order order">Expédiée le : <strong><Date :date="bordereauLivraison.expeditedAt" /></strong></li>
                    <li class="woocommerce-order-overview__order order">Livrée le : <strong><Date :date="bordereauLivraison.deliveredAt" time="true" relevantTime="true"/></strong></li>
                </ul>
            </div>  

            <a :href="bordereauLivraison.status.trackingURL" target="_blank">   
                <div class="bordereau-livraison__tracking" v-if="bordereauLivraison.status.trackingURL" >
                    <i class="fa fa-truck"></i>
                    <span>
                        Cliquez ici pour suivre votre livraison en temps réel
                    </span>      
                </div>
            </a>

            <div class="woocommerce-order-details">
                <Affaire :data="bordereauLivraison.affaire" />
                <Livraison 
                    :code="bordereauLivraison.deliveryMode" 
                    :commentaire="bordereauLivraison.status.comment" />   
            </div>

            <div class="woocommerce-order-details">
                <h3>Adresse de livraison</h3>
                <Address :data="bordereauLivraison.deliveryAddress" />                
            </div> 

            <div class="bordereau-livraison__articles">

                <List class="list--table">

                    <template v-slot:checkboxActions>

                        <button class="woocommerce-button button alt"
                            :disabled="processing || !itemSelected"
                            v-on:click="buy()">
                            <span class="fa fa-shopping-cart"></span>
                                Ajouter au panier
                        </button>

                        <Button class="alt"
                            :disabled="!itemSelected"
                            :loading="processingDocuments"
                            @click.native="downloadDocs()">         
                            Télécharger les documentations
                            <i class="fa-solid fa-down-to-line"></i>
                        </Button>

                    </template>

                    <template v-slot:heading>                
                        <div class="cell cell--cb">
                            <TogglerAll />
                        </div>
                        <div class="cell cell--ref">Réf.</div>
                        <div class="cell cell--secondary">UV.</div>
                        <div class="cell cell--main">Libellé</div>
                        <div class="cell">Qté livrée</div>      
                        <div class="cell">Qté retournée</div> 
                    </template>
                    
                     <ul class="list__content">

                        <li class="list__content__item"
                            v-for="ligne in bordereauLivraison.lignes"
                            :key="ligne.id">
                                        
                            <ArticleItem
                                :data="ligne">

                                <div class="cell cell--cb">
                                    <input type="checkbox"
                                        :disabled="!ligne.articleSlug"
                                        :value="ligne"
                                        v-model="selectedItems"
                                        @click.capture="$event.stopPropagation()" />
                                </div>
                                <div class="cell cell--ref">{{ ligne.articleReference }}</div>
                                <div class="cell cell">{{ ligne.uvCodeLibelle }}</div>
                                <div class="cell cell--main">{{ ligne.libelle }}</div>
                                <div class="cell cell--qty">{{ ligne.livreeQty }}</div>
                                <div class="cell cell--qty" v-if="ligne.retourneeQty > 0">{{ ligne.retourneeQty }}</div>
                                <div class="cell cell" v-if="ligne.retourneeQty <= 0"></div> 

                            </ArticleItem>

                        </li>
                        
                    </ul>

                </List>
                
            </div>

        </article>

    </div>
</template>

<script>

import { mapState } from 'vuex'

import api from '@/api/proxyWeb/bordereauxLivraison'
import { actions } from '@/store/bordereauxLivraison'

import cart from '@/containers/cart.vue'
import title from "@/containers/title";

import Address from '@/components/address/Address.vue'
import ArticleItem from '@/components/articleItem/ArticleItem.vue'
import Badge from '@/components/badge/Badge.vue'
import Button from '@/components/button/Button.vue'
import Date from '@/components/date/Date.vue'
import List, { TogglerAll }  from '@/components/list/List.vue'
import Loader from '@/components/loader/Loader.vue'
import Livraison from '@/components/livraison/Livraison.vue'
import Affaire from '@/components/affaire/Affaire.vue'

import selectAll from '@/containers/selectAll.vue'

export default {

    name: 'Facture',

    data() {
        return {
            downloading: false,
            processingDocuments: false
        }
    },

    computed: mapState({

        bordereauLivraison: state => state.bordereauxLivraison.current,

        ready: state => ( state.bordereauxLivraison.current !== null )

    }),

    methods: {

        buy() {

            let articles = this.selectedItems.map(bordereauLivraisonLigne => {
                    return {
                        'id': bordereauLivraisonLigne.articleId,
                        'quantity': bordereauLivraisonLigne.livreeQty,
                        'variationId': bordereauLivraisonLigne.articleVariationId
                    }
                })

            this.addToCart(articles);
        },

        download() {

                if ( !! this.downloading || this.bordereauLivraison.nbDocuments < 1 ) return

                this.downloading = true;

                api.getFile(this.bordereauLivraison.id)
                    .then(() => this.$notify.success('Téléchargement en cours...') )
                    .catch(() => this.$error( 'Impossible de télécharger les documents' ))
                    .finally(() => { this.downloading = false } )                 
        },

        downloadDocs() {
            this.processingDocuments = true
            api.getDocuments(this.bordereauLivraison.id, this.selectedItems.map( bordereauLivraisonLigne => bordereauLivraisonLigne.num ))
                .then(() => { this.$notify.success('Téléchargement en cours...') })
                .finally(() => { this.processingDocuments = false } )
        }
    },
  
    created () {
        this.$store.dispatch(actions.GET, this.$route.params.id)
            .then( () => this.setTitle( 'Bordereau de livraison ' + this.bordereauLivraison.num ) )
            .catch(error => {
                this.$errorPage(error.reason)
            })
    },

    watch:{

        bordereauLivraison: function() {            

            this.selectableItems = []

            if ( !this.bordereauLivraison ) {
                return
            }
            
            this.bordereauLivraison.lignes.forEach(ligne => {
                if ( !ligne.articleSlug ) return
                this.selectableItems.push(ligne)
            })
        }
    },

    components: {
        Address,
        ArticleItem,
        Badge,
        Button,
        Date,
        List,
        Loader,
        Livraison,
        TogglerAll,
        Affaire
    },

    mixins: [ selectAll, title, cart ]
}
</script>

<style>

.bordereau-livraison {
    position: relative;
}

.bordereau-livraison__heading {
    margin-bottom: 2rem;
    padding-top: 4rem; 
}

.bordereau-livraison__heading__actions {
    position: absolute;
    right: 0;
    top: 0;
}

.bordereau-livraison__heading__actions > *:not(:first-child) {
    margin-left: 1rem !important;
}

.bordereau-livraison__footer {
}

.bordereau-livraison__footer__actions {
    text-align: right;
}

.bordereau-livraison__tracking {
    margin-top: -3rem;
    padding: 0.8rem;
    background-color: #29235C;
    font-weight: bold;
    color: white;
}

.bordereau-livraison__tracking i {
    height: 1.5rem;
    width: 1.5rem;
    font-size: 1.5rem;
    margin-right: 1rem;
}

</style>
